import React from 'react';
import PropTypes from 'prop-types';
import {
  QuantityWrapper,
  StepperButton,
  QuantityValue,
  StepperPlusIcon,
  StepperMinusIcon,
} from './Common';

const MAX_CHARS = 10;
const MAX_VALUE = 9999999999;

// Small controlled quantity stepper. Mirrors the increment/decrement clamping
// from BuyInputFields (that stepper is too Buy/Formik-coupled to reuse). Reports
// the next value through onChange — an empty string once it drops to 0, so the
// caller can treat 0 as "no quantity". The "−" button self-disables at 0.
//
// onChange's second argument reports how the value changed ('stepper' | 'typed'),
// so a caller that fires different analytics for each method (gtt_buy_qty_entry
// vs. gtt_buy_qty_stepper) doesn't have to guess from the value alone. onBlur
// fires only after the typed-entry field loses focus — a caller that fires
// analytics on blur (so typing "250" reports one event, not three) should hang
// that off onBlur rather than onChange's 'typed' branch. The stepper buttons
// have no equivalent: each click is already one discrete, complete action.
const QuantityStepper = ({ value, onChange, onBlur, max }) => {
  const current = Number(value) || 0;
  const ceiling = max ?? MAX_VALUE;
  const isMinReached = current <= 0;
  const isMaxReached = current >= ceiling;

  const handleDecrease = () => {
    if (isMinReached) return;
    const next = current - 1;
    onChange(next > 0 ? next : '', 'stepper');
  };

  const handleIncrease = () => {
    if (isMaxReached) return;
    const next = current + 1;
    if (String(next).length > MAX_CHARS) return;
    onChange(next, 'stepper');
  };

  // Free-typed entry: keep digits only, clamp length and (when set) the max, and
  // report an empty string for a cleared/zero field so gating treats it as "no
  // quantity" — consistent with the decrement path.
  const handleType = (event) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, MAX_CHARS);
    if (digits === '') {
      onChange('', 'typed');
      return;
    }
    const clamped = Math.min(Number(digits), ceiling);
    onChange(clamped > 0 ? clamped : '', 'typed');
  };

  return (
    <QuantityWrapper>
      <StepperButton
        type="button"
        onClick={handleDecrease}
        disabled={isMinReached}
        aria-label="decrease quantity"
        data-cy="gtt-quantity-decrease"
      >
        <StepperMinusIcon />
      </StepperButton>
      <QuantityValue
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="0"
        value={value === '' || value == null ? '' : current}
        onChange={handleType}
        onBlur={onBlur}
        aria-label="quantity"
        data-cy="gtt-quantity-value"
      />
      <StepperButton
        type="button"
        onClick={handleIncrease}
        disabled={isMaxReached}
        aria-label="increase quantity"
        data-cy="gtt-quantity-increase"
      >
        <StepperPlusIcon />
      </StepperButton>
    </QuantityWrapper>
  );
};

QuantityStepper.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  max: PropTypes.number,
};

export default QuantityStepper;
